import { useWorkspaceFloatingChatLayout } from "@/context/WorkspaceFloatingChatContext";
import { cn } from "@/utils";
import { motion } from "motion/react";
import type { ReactNode, RefObject } from "react";

/**
 * The scrollable `<main>` content region. Lives inside
 * `WorkspaceFloatingChatProvider` so it can reserve room on the right for the
 * full-height docked chat — narrowing the content beside the dock rather than
 * letting the dock overlap it. The reserved width is applied as inline
 * `padding-right` (it overrides the class's right gutter; the left gutter is
 * preserved), and the absolutely-positioned dock sits over that reserved strip.
 */
export function WorkspaceMainRegion({
  mainRef,
  isFullViewport,
  isGroundedSurface,
  children,
}: {
  mainRef: RefObject<HTMLDivElement | null>;
  isFullViewport: boolean;
  /** Route sits on the DS line-grid ground (home / workspace detail). */
  isGroundedSurface: boolean;
  children: ReactNode;
}) {
  const { dockReservedWidthPx } = useWorkspaceFloatingChatLayout();

  return (
    <main
      className={cn(
        // relative: paints above the ground's positioned grid layers.
        "relative box-border min-h-0 flex-1",
        "@container",
        "scrollbar-hide overflow-auto",
        // Transparent on grounded routes — the grid lives on the wrapper so
        // it holds still while the page scrolls over it.
        isGroundedSurface ? "w-full" : "w-full bg-surface-main",
        !isFullViewport && "px-4 sm:px-[16px] md:px-[32px]",
      )}
      style={
        dockReservedWidthPx > 0
          ? { paddingRight: dockReservedWidthPx + 16 }
          : undefined
      }
      ref={mainRef}
    >
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative mx-auto flex h-full flex-col"
      >
        <nav id="navigation-slot" />
        {children}
      </motion.div>
    </main>
  );
}
